import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';

const DIFFICULTY_CONFIG = {
  easy: { color: '#10B981', bg: '#ECFDF5', emoji: '🌱', label: 'Easy' },
  medium: { color: '#F59E0B', bg: '#FEF3C7', emoji: '⭐', label: 'Medium' },
  hard: { color: '#EF4444', bg: '#FEF2F2', emoji: '🔥', label: 'Hard' },
};

const ChoreSuggestions = ({ kidId, onCreateChore }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'indoor', 'outdoor', 'learning', 'creative', 'helping'];

  const fetchSuggestions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const catParam = selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
      const response = await axios.get(`/api/v1/chore-suggestions?kid_id=${kidId}${catParam}`);
      setSuggestions(response.data.suggestions || []);
    } catch (err) {
      setError('Could not load suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [kidId, selectedCategory]);

  useEffect(() => {
    if (kidId) fetchSuggestions();
  }, [kidId, fetchSuggestions]);

  return (
    <div className="chore-suggestions-panel">
      <div className="suggestions-header">
        <div className="suggestions-header-left">
          <span className="suggestions-icon">🤖</span>
          <div>
            <h3 className="suggestions-title">Smart Chore Ideas</h3>
            <p className="suggestions-subtitle">AI-powered suggestions based on your kid's interests</p>
          </div>
        </div>
        <button className="btn-refresh" onClick={fetchSuggestions} title="Get new suggestions">
          🔄
        </button>
      </div>

      {/* Category filter */}
      <div className="category-filter">
        {categories.map(cat => (
          <button
            key={cat}
            className={`category-chip ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="suggestions-loading">
          <div className="spinner" />
          <p>Thinking of great chores...</p>
        </div>
      ) : error ? (
        <div className="suggestions-error">
          <span>😕</span>
          <p>{error}</p>
          <button className="btn-retry" onClick={fetchSuggestions}>Try Again</button>
        </div>
      ) : (
        <div className="suggestions-grid">
          {suggestions.map((s, idx) => {
            const config = DIFFICULTY_CONFIG[s.difficulty] || DIFFICULTY_CONFIG.medium;
            return (
              <div key={idx} className="suggestion-card" onClick={() => onCreateChore?.(s)}>
                <div className="suggestion-card-header">
                  <span className="suggestion-emoji">{config.emoji}</span>
                  <h4 className="suggestion-title">{s.title}</h4>
                  <span
                    className="difficulty-badge"
                    style={{ backgroundColor: config.bg, color: config.color }}
                  >
                    {config.label}
                  </span>
                </div>
                <p className="suggestion-desc">{s.description}</p>
                <div className="suggestion-footer">
                  <span className="reward-badge">💰 {s.reward_min}-{s.reward_max} DB</span>
                  <span className="time-badge">⏱ {s.estimated_minutes} min</span>
                  <span className="add-link">+ Add as Chore</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .chore-suggestions-panel {
          background: #fff;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          margin-bottom: 24px;
        }
        .suggestions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .suggestions-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .suggestions-icon {
          font-size: 28px;
          background: linear-gradient(135deg, #7C3AED, #A855F7);
          padding: 8px;
          border-radius: 12px;
        }
        .suggestions-title {
          font-size: 18px;
          font-weight: 700;
          color: #1F2937;
          margin: 0;
        }
        .suggestions-subtitle {
          font-size: 12px;
          color: #9CA3AF;
          margin: 2px 0 0;
        }
        .btn-refresh {
          background: #F3F4F6;
          border: none;
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 16px;
          cursor: pointer;
          transition: background 0.2s;
        }
        .btn-refresh:hover { background: #E5E7EB; }
        .category-filter {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .category-chip {
          padding: 6px 14px;
          border-radius: 20px;
          border: none;
          background: #F3F4F6;
          color: #6B7280;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .category-chip.active {
          background: #7C3AED;
          color: #fff;
        }
        .category-chip:hover:not(.active) { background: #E5E7EB; }
        .suggestions-loading {
          text-align: center;
          padding: 40px;
          color: #9CA3AF;
        }
        .spinner {
          width: 32px; height: 32px;
          border: 3px solid #E5E7EB;
          border-top-color: #7C3AED;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 12px;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .suggestions-error { text-align: center; padding: 32px; }
        .suggestions-error span { font-size: 40px; }
        .btn-retry {
          background: #7C3AED; color: #fff; border: none;
          padding: 8px 20px; border-radius: 20px; cursor: pointer;
          font-weight: 600; margin-top: 12px;
        }
        .suggestions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 12px;
        }
        .suggestion-card {
          background: #FAFAFA;
          border: 1px solid #F3F4F6;
          border-radius: 14px;
          padding: 16px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .suggestion-card:hover {
          border-color: #7C3AED;
          box-shadow: 0 4px 12px rgba(124, 58, 237, 0.1);
          transform: translateY(-2px);
        }
        .suggestion-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        .suggestion-emoji { font-size: 20px; }
        .suggestion-title {
          flex: 1;
          font-size: 15px;
          font-weight: 600;
          color: #1F2937;
          margin: 0;
        }
        .difficulty-badge {
          font-size: 11px;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 12px;
          text-transform: uppercase;
        }
        .suggestion-desc {
          font-size: 13px;
          color: #6B7280;
          line-height: 1.4;
          margin: 0 0 12px;
        }
        .suggestion-footer {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .reward-badge {
          background: #FEF3C7;
          color: #92400E;
          font-size: 12px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 12px;
        }
        .time-badge {
          background: #EDE9FE;
          color: #5B21B6;
          font-size: 12px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 12px;
        }
        .add-link {
          margin-left: auto;
          color: #7C3AED;
          font-size: 13px;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default ChoreSuggestions;
